import { PROGRESS_GOLD } from '@sorrem-client/ui/shared/palette';
import classnames from 'classnames';
import { canPayAbilityEssenceCost, findEssenceAbilityForSkill, findSkillCatalogEntry, resolveAbilityEssenceCost } from '@sorrem/shared';
import type { ActionBarHotkeyTarget, CharacterEquipmentLoadout, Skill } from '@sorrem/shared';
import { useEffect, useMemo, useState } from 'react';
import type { UIAbilityCooldown, UICoalescence, UIInventoryItem } from '@sorrem-client/types';
import { CarriedSlotContent, TimerRing } from '@sorrem-client/ui/components';
import { CrystalActionSummary, EquipmentSummary, SkillActionSummary } from '@sorrem-client/ui/features/ActionSlotSummary';
import { resolveComparedEquipment, toEquipmentSlots } from '@sorrem-client/ui/shared/equipment-slots';
import { hasSkillTreeSkillIcon, SkillTreeSkillIcon } from '@sorrem-client/ui/features/SkillTree';
import styles from './loadout-actions.module.scss';


/** Whose action bar it is: what they hold and what changes their Essence abilities' costs. */
export interface ActionBarCaster {
    essence: number;
    skills: readonly Skill[];
    wieldedLoadout: CharacterEquipmentLoadout | undefined;
}

/** Whether `caster` holds too little essence to cast the ability of `skillId`, at their own cost. */
function isShortOfEssence(skillId: string, caster: ActionBarCaster | undefined): boolean {
    const ability = findEssenceAbilityForSkill(skillId);
    if (caster === undefined || ability === undefined) {
        return false;
    }
    return !canPayAbilityEssenceCost(caster.essence, resolveAbilityEssenceCost(ability, caster.skills, caster.wieldedLoadout));
}

interface SkillActionLabelProps {
    skillId: string;
    cooldown: UIAbilityCooldown | undefined;
    /** The character holds too little essence to cast it. */
    isShort: boolean;
}

/**
 * A skill's action frame: its tree emblem (or, for a skill with none, its
 * ability's name). Until the ability is ready again it is dimmed, with a ring
 * draining away around it and the seconds left in the middle. While the
 * character can't pay for it, it is drained to a cold violet instead, so the
 * two read apart (and stack). The name shows on hover either way.
 */
function SkillActionLabel({ skillId, cooldown, isShort }: SkillActionLabelProps): JSX.Element {
    const readyAtMs = cooldown?.readyAtMs;
    const [isCoolingDown, setIsCoolingDown] = useState(() => readyAtMs !== undefined && readyAtMs > Date.now());

    useEffect(() => {
        const remainingMs = (readyAtMs ?? 0) - Date.now();
        setIsCoolingDown(remainingMs > 0);
        if (remainingMs <= 0) {
            return undefined;
        }
        const timeout = setTimeout(() => setIsCoolingDown(false), remainingMs);
        return () => clearTimeout(timeout);
    }, [readyAtMs]);

    const abilityName = findEssenceAbilityForSkill(skillId)?.name ?? findSkillCatalogEntry(skillId)?.name ?? skillId;
    const name = isShort ? `${abilityName} (not enough essence)` : abilityName;
    const ring = isCoolingDown && cooldown !== undefined && (
        <TimerRing startedAtMs={cooldown.startedAtMs} endsAtMs={cooldown.readyAtMs} color={PROGRESS_GOLD} mode='drain' secondsPlacement='center' />
    );
    if (!hasSkillTreeSkillIcon(skillId)) {
        return (
            <>
                <span className={classnames(styles.label, isCoolingDown && styles.dim, isShort && styles.short)} title={name}>{abilityName}</span>
                {ring}
            </>
        );
    }
    return (
        <>
            <span className={classnames(styles.skill, isCoolingDown && styles.dim, isShort && styles.short)} title={name}>
                <SkillTreeSkillIcon skillId={skillId} className={styles.icon} />
            </span>
            {ring}
        </>
    );
}

/**
 * Content for the loadout's nine action frames, from the account's hotkey
 * mapping and the carried inventory. A frame linked to a carried slot shows
 * exactly what the Carried panel shows for that slot: its item, a stack's
 * count, and any crystal coalescing into it. A skill's frame shows when
 * `caster` can't pay for its ability.
 */
export function buildActionBarSlots(
    hotkeys: readonly ActionBarHotkeyTarget[],
    carriedItems: ReadonlyArray<UIInventoryItem | undefined>,
    caster: ActionBarCaster | undefined,
    coalescences: readonly UICoalescence[] = [],
    abilityCooldowns: Readonly<Record<string, UIAbilityCooldown>> = {},
): Array<JSX.Element | null> {
    return hotkeys.map((target) => {
        if (target === null || target === undefined) {
            return null;
        }
        switch (target.kind) {
            case 'skill':
                return <SkillActionLabel skillId={target.skillId} cooldown={abilityCooldowns[target.skillId]} isShort={isShortOfEssence(target.skillId, caster)} />;
            case 'carried-slot': {
                const item = carriedItems[target.slotIndex];
                const pending = findPendingCoalescences(coalescences, target.slotIndex);
                if (item === undefined && pending.length === 0) {
                    // Still linked; shows which carried slot it'll trigger once something occupies it.
                    return <span className={classnames(styles.label, styles.dim)} title={`Carried slot ${target.slotIndex + 1} (empty)`}>{target.slotIndex + 1}</span>;
                }
                return <CarriedSlotContent item={item} pending={pending} />;
            }
            default: {
                const unknownTarget: never = target;
                return unknownTarget;
            }
        }
    });
}

/**
 * What each of the loadout's nine action frames does, for the popover shown
 * while the pointer rests on one: a skill's Essence ability, a crystal
 * stack's effect, or a piece of equipment compared with what pressing the
 * frame would swap out. Null for an empty frame, or one linked to an empty
 * carried slot. An ability shows the cost and cooldown `skills` and the
 * wielded gear give it.
 */
export function buildActionBarDetails(
    hotkeys: readonly ActionBarHotkeyTarget[],
    carriedItems: ReadonlyArray<UIInventoryItem | undefined>,
    equipped: CharacterEquipmentLoadout | undefined,
    skills: readonly Skill[],
    coalescences: readonly UICoalescence[] = [],
): Array<JSX.Element | null> {
    const equipmentSlots = equipped === undefined ? [] : toEquipmentSlots(equipped);
    return hotkeys.map((target) => {
        if (target === null || target === undefined) {
            return null;
        }
        switch (target.kind) {
            case 'skill':
                return <SkillActionSummary skillId={target.skillId} skills={skills} equipped={equipped} />;
            case 'carried-slot': {
                const item = carriedItems[target.slotIndex];
                const pending = findPendingCoalescences(coalescences, target.slotIndex);
                if (item === undefined || item.crystal !== undefined) {
                    return pending.length === 0 && item === undefined ? null : <CrystalActionSummary item={item} pending={pending} />;
                }
                return <EquipmentSummary item={item} compared={resolveComparedEquipment(item, equipmentSlots)} />;
            }
            default: {
                const unknownTarget: never = target;
                return unknownTarget;
            }
        }
    });
}

/** Coalescences headed for a carried slot, soonest first. */
function findPendingCoalescences(coalescences: readonly UICoalescence[], slotIndex: number): UICoalescence[] {
    return coalescences
        .filter((coalescence) => coalescence.targetSlotIndex === slotIndex)
        .sort((a, b) => a.completesAtMs - b.completesAtMs);
}

const NO_COALESCENCES: readonly UICoalescence[] = [];
const NO_ABILITY_COOLDOWNS: Readonly<Record<string, UIAbilityCooldown>> = {};
const NO_SKILLS: readonly Skill[] = [];

/** A `Loadout`'s action frame props: what each of the nine frames shows, and its popover details. */
export interface LoadoutActions {
    actions: Array<JSX.Element | null>;
    actionDetails: Array<JSX.Element | null>;
}

/**
 * The action frames, the same in a run, at a gathering and in Character
 * Home (see `buildActionBarSlots` and `buildActionBarDetails`). Pass a
 * memoized `caster`; its gear and skills also set what the details show.
 */
export function useLoadoutActions(
    hotkeys: readonly ActionBarHotkeyTarget[],
    carriedItems: ReadonlyArray<UIInventoryItem | undefined>,
    caster: ActionBarCaster | undefined,
    coalescences: readonly UICoalescence[] = NO_COALESCENCES,
    abilityCooldowns: Readonly<Record<string, UIAbilityCooldown>> = NO_ABILITY_COOLDOWNS,
): LoadoutActions {
    const actions = useMemo(
        () => buildActionBarSlots(hotkeys, carriedItems, caster, coalescences, abilityCooldowns),
        [hotkeys, carriedItems, caster, coalescences, abilityCooldowns],
    );
    const wieldedLoadout = caster?.wieldedLoadout;
    const skills = caster?.skills ?? NO_SKILLS;
    const actionDetails = useMemo(
        () => buildActionBarDetails(hotkeys, carriedItems, wieldedLoadout, skills, coalescences),
        [hotkeys, carriedItems, wieldedLoadout, skills, coalescences],
    );
    return { actions, actionDetails };
}
